import type { DataSource } from '../../types/siteRag';

/**
 * Reliability-marker policy (FIX 2, 2026-10-13). One rule shared by the
 * source-role detector (`checkSourceRoleText`) and the deterministic
 * verified→inferred correction (`reliabilityMarkerCorrection.ts`), so the two
 * can never disagree about which citation member may carry `verified`.
 *
 * Types only: this module never imports the store, a detector or a renderer.
 */
export const RELIABILITY_MARKER_POLICY_VERSION = 'reliability-marker-policy-v1' as const;

export type ReliabilityMarkerLabel = 'verified' | 'inferred' | 'unverified';

/** A recorded estimate, assumption or unqualified source cannot carry a
 * `verified` reliability marker. Missing/out-of-range references never match. */
export function referenceForbidsVerifiedMarker(
  reference: Pick<DataSource, 'sourceGrade' | 'qualification'> | null | undefined,
): boolean {
  if (!reference) return false;
  return reference.sourceGrade === 'inference'
    || reference.qualification === 'estimated'
    || reference.qualification === 'unknown'
    || reference.qualification === 'unavailable';
}

export interface NumberedRefTokenMember {
  /** Zero-based position among the token's comma-separated parts. */
  position: number;
  /** Label written on this member, lower-cased. */
  explicitLabel?: ReliabilityMarkerLabel;
  /** Label in force for this member: a label applies to the members after it
   * until the next label; members before the first label have none. */
  effectiveLabel?: ReliabilityMarkerLabel;
  /** One-based reference ordinal as cited. */
  n: number;
  /** The exact digits as written, so a rebuild never re-spells the number. */
  digits: string;
}

const MEMBER_RE = /^\s*(?:(verified|inferred|unverified)\s+)?(?:Ref\s*(?::\s*)?)?(\d+)\s*$/i;

/** Parse one `NUMBERED_REF_TOKEN_RE` match (`[verified Ref 4, Ref 9]`) into
 * its members with the detector's forward label inheritance. A part outside
 * the member grammar is skipped, exactly as the detector skips it. */
export function parseNumberedRefTokenMembers(token: string): NumberedRefTokenMember[] {
  const inner = token.startsWith('[') && token.endsWith(']') ? token.slice(1, -1) : token;
  const members: NumberedRefTokenMember[] = [];
  let inherited: ReliabilityMarkerLabel | undefined;
  inner.split(',').forEach((part, position) => {
    const parsed = MEMBER_RE.exec(part);
    if (!parsed) return;
    const explicitLabel = parsed[1] ? parsed[1].toLowerCase() as ReliabilityMarkerLabel : undefined;
    if (explicitLabel) inherited = explicitLabel;
    members.push({
      position,
      ...(explicitLabel ? { explicitLabel } : {}),
      ...(inherited ? { effectiveLabel: inherited } : {}),
      n: Number(parsed[2]),
      digits: parsed[2]!,
    });
  });
  return members;
}

export type ReliabilityMarkerCorrectionStage =
  | 'post-generation'
  | 'agent-post-generation'
  | 'final-source-validation'
  | 'final-repair';

/** One relabelled citation member. Deliberately carries no citation text, no
 * `severity` and no claim/evidence shape: it is never a finding and never
 * rewritten by reference renumbering. */
export interface ReliabilityMarkerCorrectionRow {
  /** Report path of the corrected string, e.g. `btmOptions[1].reason`. */
  fieldPath: string;
  /** Zero-based numbered-citation token index within that string. */
  tokenOrdinalInField: number;
  /** Zero-based member position within that token. */
  memberPosition: number;
  /** One-based reference ordinal at the correction stage (pre-renumbering). */
  referenceOrdinalAtStage: number;
  /** Identity of that reference at the correction stage. */
  referenceIdentity: { field: string; source: string; retrievedAt: string };
  sourceGrade: DataSource['sourceGrade'] | null;
  qualification: DataSource['qualification'] | null;
  fromLabel: 'verified';
  toLabel: 'inferred';
  /** sha256 of the whole field before / after the correction. */
  beforeHash: string;
  afterHash: string;
}

export interface ReliabilityMarkerCorrectionReceipt {
  version: typeof RELIABILITY_MARKER_POLICY_VERSION;
  stage: ReliabilityMarkerCorrectionStage;
  sedId: string | null;
  sedVersion: number | null;
  /** Canonical hash of the reference list the ordinals refer to. */
  referencesHash: string;
  /** Capped; `truncatedRowCount` records how many further rows were omitted. */
  rows: ReliabilityMarkerCorrectionRow[];
  truncatedRowCount: number;
}
